/**
 * Shared React hooks for Power Reporting components
 * Centralized to avoid code duplication
 */

import { useState, useEffect, useRef, useCallback } from 'react';

// Cache size limits to prevent memory leaks in long sessions
const MAX_CACHE_SIZE = 50;

/**
 * Helper to enforce cache size limit (LRU-style - removes oldest entries)
 * @param {Map} cache - The cache Map to manage
 * @param {number} maxSize - Maximum number of entries
 */
const enforceCacheLimit = (cache, maxSize = MAX_CACHE_SIZE) => {
  while (cache.size >= maxSize) {
    // Map maintains insertion order, so first key is oldest
    const oldestKey = cache.keys().next().value;
    cache.delete(oldestKey);
  }
};

/**
 * Helper to clean expired entries from cache
 * @param {Map} cache - The cache Map to clean
 */
const cleanExpiredEntries = (cache) => {
  const now = Date.now();
  for (const [key, value] of cache.entries()) {
    if (value.expiresAt && now > value.expiresAt) {
      cache.delete(key);
    }
  }
};

/**
 * Hook for detecting mobile viewport (< 768px)
 * Includes debounced resize handling for performance
 * @returns {boolean} True if viewport is mobile width
 */
export const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    let timeoutId;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setIsMobile(window.innerWidth < 768);
      }, 100); // Debounce resize events
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  return isMobile;
};

/**
 * Simple hook for debouncing any value
 * @param {any} value - Value to debounce
 * @param {number} delay - Debounce delay in ms
 * @returns {any} Debounced value
 */
export const useDebounce = (value, delay = 400) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [value, delay]);

  return debouncedValue;
};

/**
 * Hook for debounced search input with full control
 * @param {string} initialValue - Initial search value
 * @param {number} delay - Debounce delay in ms
 * @returns {[string, string, function, function]} [debouncedValue, immediateValue, setSearchValue, clearSearch]
 */
export const useDebouncedSearch = (initialValue = '', delay = 400) => {
  const [immediateValue, setImmediateValue] = useState(initialValue);
  const [debouncedValue, setDebouncedValue] = useState(initialValue);
  const timeoutRef = useRef(null);

  const setSearchValue = useCallback((value) => {
    setImmediateValue(value);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
  }, [delay]);

  const clearSearch = useCallback(() => {
    setImmediateValue('');
    setDebouncedValue('');
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return [debouncedValue, immediateValue, setSearchValue, clearSearch];
};

/**
 * Simple in-memory cache for PDF data
 * Stores base64 PDF data by reportId with expiration
 */
const pdfCache = new Map();
const PDF_CACHE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Hook for caching PDF data
 * @returns {Object} { getCachedPdf, setCachedPdf, clearPdfCache }
 */
export const usePdfCache = () => {
  const getCachedPdf = useCallback((reportId) => {
    const cached = pdfCache.get(reportId);
    if (!cached) return null;

    // Check if expired
    if (Date.now() > cached.expiresAt) {
      pdfCache.delete(reportId);
      return null;
    }

    return cached.data;
  }, []);

  const setCachedPdf = useCallback((reportId, pdfData) => {
    cleanExpiredEntries(pdfCache);
    enforceCacheLimit(pdfCache);
    pdfCache.set(reportId, {
      data: pdfData,
      expiresAt: Date.now() + PDF_CACHE_EXPIRY_MS
    });
  }, []);

  const clearPdfCache = useCallback(() => {
    pdfCache.clear();
  }, []);

  return { getCachedPdf, setCachedPdf, clearPdfCache };
};

/**
 * Hook to track mouse position for distinguishing clicks from drags
 * @returns {Object} { handleMouseDown, isClick }
 */
export const useClickVsDrag = () => {
  const mouseDownPos = useRef(null);

  const handleMouseDown = useCallback((e) => {
    mouseDownPos.current = { x: e.clientX, y: e.clientY };
  }, []);

  const isClick = useCallback((e) => {
    if (!mouseDownPos.current) return true;

    const moved = Math.abs(mouseDownPos.current.x - e.clientX) > 5 ||
                  Math.abs(mouseDownPos.current.y - e.clientY) > 5;
    mouseDownPos.current = null;

    return !moved;
  }, []);

  return { handleMouseDown, isClick };
};

/**
 * Simple data cache hook for API responses
 * @param {string} cacheKey - Unique key for caching
 * @param {number} expiryMs - Cache expiry time in milliseconds (default 2 minutes)
 * @returns {Object} { getCached, setCached, clearCache }
 */
const dataCache = new Map();

export const useDataCache = (cacheKey, expiryMs = 2 * 60 * 1000) => {
  const getCached = useCallback(() => {
    const cached = dataCache.get(cacheKey);
    if (!cached) return null;

    if (Date.now() > cached.expiresAt) {
      dataCache.delete(cacheKey);
      return null;
    }

    return cached.data;
  }, [cacheKey]);

  const setCached = useCallback((data) => {
    cleanExpiredEntries(dataCache);
    enforceCacheLimit(dataCache);
    dataCache.set(cacheKey, {
      data,
      expiresAt: Date.now() + expiryMs
    });
  }, [cacheKey, expiryMs]);

  const clearCache = useCallback(() => {
    dataCache.delete(cacheKey);
  }, [cacheKey]);

  return { getCached, setCached, clearCache };
};

/**
 * Available Powers Cache
 * Singleton cache for available powers data per agency
 * Cached for 2 minutes to reduce API calls when opening modal repeatedly
 */
const availablePowersCache = new Map();
const POWERS_CACHE_EXPIRY_MS = 2 * 60 * 1000; // 2 minutes

export const useAvailablePowersCache = () => {
  const getCachedPowers = useCallback((agencyId) => {
    if (!agencyId) return null;

    const cached = availablePowersCache.get(agencyId);
    if (!cached) return null;

    // Check if expired
    if (Date.now() > cached.expiresAt) {
      availablePowersCache.delete(agencyId);
      return null;
    }

    return cached.data;
  }, []);

  const setCachedPowers = useCallback((agencyId, powers) => {
    if (!agencyId) return;

    cleanExpiredEntries(availablePowersCache);
    enforceCacheLimit(availablePowersCache);
    availablePowersCache.set(agencyId, {
      data: powers,
      expiresAt: Date.now() + POWERS_CACHE_EXPIRY_MS
    });
  }, []);

  const invalidatePowersCache = useCallback((agencyId) => {
    if (agencyId) {
      availablePowersCache.delete(agencyId);
    } else {
      availablePowersCache.clear();
    }
  }, []);

  return { getCachedPowers, setCachedPowers, invalidatePowersCache };
};

/**
 * Agents and Cities Cache
 * Caches executing agents and cities data per agency
 * These rarely change so cached for 5 minutes
 */
const agentsCache = new Map();
const citiesCache = new Map();
const AGENTS_CITIES_CACHE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

export const useAgentsCitiesCache = () => {
  const getCachedAgents = useCallback((agencyId) => {
    if (!agencyId) return null;
    const cached = agentsCache.get(agencyId);
    if (!cached || Date.now() > cached.expiresAt) {
      agentsCache.delete(agencyId);
      return null;
    }
    return cached.data;
  }, []);

  const setCachedAgents = useCallback((agencyId, agents) => {
    if (!agencyId) return;
    cleanExpiredEntries(agentsCache);
    enforceCacheLimit(agentsCache);
    agentsCache.set(agencyId, {
      data: agents,
      expiresAt: Date.now() + AGENTS_CITIES_CACHE_EXPIRY_MS
    });
  }, []);

  const getCachedCities = useCallback((agencyId) => {
    if (!agencyId) return null;
    const cached = citiesCache.get(agencyId);
    if (!cached || Date.now() > cached.expiresAt) {
      citiesCache.delete(agencyId);
      return null;
    }
    return cached.data;
  }, []);

  const setCachedCities = useCallback((agencyId, cities) => {
    if (!agencyId) return;
    cleanExpiredEntries(citiesCache);
    enforceCacheLimit(citiesCache);
    citiesCache.set(agencyId, {
      data: cities,
      expiresAt: Date.now() + AGENTS_CITIES_CACHE_EXPIRY_MS
    });
  }, []);

  const invalidateAgentsCitiesCache = useCallback((agencyId) => {
    if (agencyId) {
      agentsCache.delete(agencyId);
      citiesCache.delete(agencyId);
    } else {
      agentsCache.clear();
      citiesCache.clear();
    }
  }, []);

  return {
    getCachedAgents,
    setCachedAgents,
    getCachedCities,
    setCachedCities,
    invalidateAgentsCitiesCache
  };
};
